import { ComponentType, ReactNode, useEffect } from 'react';
import { Link } from 'ui/links';
import styled from 'styled-components';
import { pxToRem } from 'utils/convert';
import { EmptyState, EmptyStateFind, NoResult, EmptyStateEnvelope } from 'ui/illustrations';
import { spacing, colors } from 'ui/theme';
import { body1, heading3, heading4 } from 'ui/theme/typography';
import { snippetUrlList, snippetUrlNew, snippetSupportUrl } from '../constants';

const EmptyStateWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${pxToRem(400)};
`;

const EmptyStateContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: ${pxToRem(461)};
`;

const MainStyling = styled.div`
  margin-bottom: ${spacing.half};
  ${heading3};
`;

const CaptionStyling = styled.div`
  text-align: center;
  ${body1};
  color: ${colors.textSecondary};
`;

const ModalEmptyStateContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${colors.white};
  padding: ${pxToRem(80)};
  margin-top: ${spacing.plusHalf};
  width: ${pxToRem(621)};
`;

const ModalMainStyling = styled(MainStyling)`
  ${heading4};
`;

type ImageProps = {
  height?: number;
  width?: number;
};
interface EmptyStateGeneratorProps {
  Image: ComponentType<React.PropsWithChildren<ImageProps>>;
  mainText: string;
  id: string;
  children: ReactNode;
}

const EmptyStateGenerator = ({ Image, mainText, id, children }: EmptyStateGeneratorProps) => {
  useEffect(() => {
    document.title = `${mainText} | Snippets`;
    return () => {
      document.title = 'Snippets';
    };
  }, [mainText]);

  return (
    <EmptyStateWrapper data-test={id}>
      <EmptyStateContent>
        <Image />
        <MainStyling>{mainText}</MainStyling>
        <CaptionStyling>{children}</CaptionStyling>
      </EmptyStateContent>
    </EmptyStateWrapper>
  );
};

export const EmptyStateNotFound = (): JSX.Element => (
  <EmptyStateGenerator
    Image={EmptyStateFind}
    mainText="Oops... we couldn't find that snippet"
    id="snippet-edit-empty-state"
  >
    <div>
      <p>It may have been deleted, or there could be a typo in the URL.</p>
      <Link useReactRouter href={snippetUrlList}>
        Back to All Snippets
      </Link>
    </div>
  </EmptyStateGenerator>
);

export const GeneralEmptyState = (): JSX.Element => (
  <EmptyStateGenerator Image={EmptyState} mainText="No snippets yet" id="snippet-index-empty-state">
    <div>
      Learn more by visiting our{' '}
      <Link size="body1" href={'https://support.iterable.com/'}>
        Knowledge Center
      </Link>
      .
    </div>
  </EmptyStateGenerator>
);

export const NoResults = (): JSX.Element => (
  <EmptyStateGenerator
    Image={NoResult}
    mainText="No matching results"
    id="snippet-index-no-results"
  >
    {'Try searching by different keywords.'}
  </EmptyStateGenerator>
);

export const NoTemplates = () => (
  <EmptyStateGenerator
    Image={EmptyStateEnvelope}
    mainText="Hmm, no locations found."
    id="snippet-search-empty-state"
  >
    <div>
      This snippet may not be in use anywhere, or check out our{' '}
      <Link href={snippetSupportUrl} target="_blank">
        support documentation
      </Link>{' '}
      for a list of currently known feature limitations.
    </div>
  </EmptyStateGenerator>
);

export const NoSnippets = () => (
  <EmptyStateWrapper>
    <ModalEmptyStateContent>
      <EmptyState height={spacing.x8} width={spacing.x8} />
      <ModalMainStyling>{"You'll find your snippets here"}</ModalMainStyling>

      <CaptionStyling>
        To create a reusable content block that you can add to your templates,{' '}
        <Link useReactRouter hasOpenNewTabIcon href={snippetUrlNew}>
          add a new snippet
        </Link>
      </CaptionStyling>
    </ModalEmptyStateContent>
  </EmptyStateWrapper>
);
